/**
 * Caption rewrite helper — used by PostBody's "Tell Mora what to change"
 * composer to iterate on a post caption IN PLACE (no nav, no chat
 * round-trip). Mirrors `caption-suggest.ts` but takes an existing caption
 * + instruction and returns a rewrite.
 *
 * Auth: endpoint requires a Bearer token. Caller MUST pass `accessToken`.
 *
 * Replaces the prior PostBody behaviour that called
 * `navigate('/?editPost=…&prefill=…')` and dumped the user into chat —
 * image and video iterate in place via their stores; posts now do too.
 */

import type { ErrorCategory } from '@/lib/errors/friendly-errors';

export type RewritePlatform =
  | 'instagram'
  | 'tiktok'
  | 'twitter'
  | 'linkedin'
  | 'facebook'
  | 'threads'
  | 'pinterest'
  | 'youtube';

/**
 * Carries the server's machine-readable failure shape to the UI layer.
 *
 * The previous behaviour rethrew the server's raw `error` string, which on a
 * 502 is whatever exception the provider SDK happened to throw — leaked
 * verbatim into a toast. The status → category mapping here is the seam the
 * toast reads instead; `retryAfter` is the value the server already computes
 * and the client previously ignored.
 */
export class CaptionRewriteError extends Error {
  readonly name = 'CaptionRewriteError';
  readonly errorCode: ErrorCategory;
  readonly retryAfter?: number;
  readonly status: number;

  constructor(args: {
    message: string;
    errorCode: ErrorCategory;
    status: number;
    retryAfter?: number;
  }) {
    super(args.message);
    this.errorCode = args.errorCode;
    this.status = args.status;
    this.retryAfter = args.retryAfter;
  }
}

function categoryForStatus(status: number): ErrorCategory {
  if (status === 429) return 'rate_limit';
  if (status === 401 || status === 403) return 'auth';
  if (status === 408 || status === 504) return 'timeout';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

function parseRetryAfter(body: unknown, header: string | null): number | undefined {
  const fromBody = (body as { retryAfter?: unknown } | null)?.retryAfter;
  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody > 0) {
    return Math.ceil(fromBody);
  }
  const fromHeader = header ? Number(header) : NaN;
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader);
  return undefined;
}

export async function rewriteCaption(args: {
  original: string;
  instruction: string;
  accessToken: string;
  platform?: RewritePlatform;
}): Promise<string> {
  const { original, instruction, accessToken, platform } = args;
  // LOCAL pre-flight failures stay bare `Error`s on purpose — see the review
  // correction below. Only the SERVER's failure shape gets the typed error.
  if (!accessToken) {
    throw new Error('Caption rewrite requires an access token');
  }
  if (!original.trim() || !instruction.trim()) {
    throw new Error('Caption rewrite requires both original text and instruction');
  }
  const response = await fetch('/api/content/rewrite', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ original, instruction, platform }),
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new CaptionRewriteError({
      message: data?.error || `Caption rewrite failed: ${response.status}`,
      errorCode: categoryForStatus(response.status),
      status: response.status,
      retryAfter: parseRetryAfter(data, response.headers.get('Retry-After')),
    });
  }
  const data = (await response.json()) as { rewritten?: string };
  if (typeof data.rewritten !== 'string' || data.rewritten.length === 0) {
    throw new CaptionRewriteError({
      message: 'Caption rewrite returned empty response',
      errorCode: 'server_error',
      status: response.status,
    });
  }
  return data.rewritten;
}
